/**
 * The renderer's reading of the ONE Files gate (plan §3.7, Tasks 7.13–7.15): every tree item, toolbar button, row-menu
 * entry and drop target that can refuse asks `fileActionReason`, which applies main's order — read-only (writes) →
 * attachment rule (`fileAttachmentRule`) → `fileOperationGate` (snapshot → credential → not offered → unknown protocol →
 * protocol) — over the pure tables in `azureStorageFileCoverage.ts`. `undefined` = allowed; a string is the tooltip.
 * Main re-checks every request; this only keeps the UI from offering what main would refuse.
 */
import type { Connection } from '../../../../types/electron';
import type { FileShareOperation, FileShareProtocolState } from '../../../../types/azureStorage';
import { fileAttachmentRule, fileOperationGate, isFileWriteOperation, type FileCredentialClass } from '../../../../core/engines/azureStorageFileCoverage';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';

export type FileGateConnection = Pick<Connection, 'authentication' | 'readOnly' | 'azureStorageScope' | 'azureStorageDataAccess' | 'azureStorageEmulator'>;

/**
 * The connection's credential class as far as the renderer can tell without the secret (main's `credentialClass`):
 * a SAS rooted at a share / folder / file is a share SAS (service SAS), any other SAS an account SAS; Entra with the opt-in
 * account-key data access is Shared Key once connected.
 */
export function fileCredentialClassOf(connection: FileGateConnection): FileCredentialClass {
  const scope = connection.azureStorageScope;
  const scoped = scope?.kind === 'resource';
  switch (connection.authentication as string) {
    case 'Account Key':
      return 'shared-key';
    case 'Anonymous':
      return 'anonymous';
    case 'Shared Access Signature':
      return scoped && ['share', 'directory', 'file'].includes(scope.resource.kind) ? 'service-sas' : 'account-sas';
    default:
      return connection.azureStorageDataAccess === 'account-key-via-arm' && !scoped && !connection.azureStorageEmulator ? 'shared-key' : 'entra';
  }
}

export type FileGateContext = {
  protocol?: FileShareProtocolState;
  snapshot?: string;
  /** Stored access policies open view-only on a read-only connection (S3 rule); the dialog disables its writes. */
  ignoreReadOnly?: boolean;
};

/** Why `op` is unavailable on this connection, or `undefined` when the UI may offer it. */
export function fileActionReason(connection: FileGateConnection | undefined, op: FileShareOperation, context: FileGateContext = {}): string | undefined {
  if (!connection) return 'This connection is no longer available.';
  const readOnly = !context.ignoreReadOnly && connection.readOnly === true;
  if (readOnly && isFileWriteOperation(op)) return STORAGE_READ_ONLY_REASON;
  const rule = fileAttachmentRule(connection.azureStorageScope ?? { kind: 'account' }, op);
  if (!rule.allowed) return rule.reason;
  const gate = fileOperationGate(op, {
    protocol: context.protocol ?? 'unknown',
    snapshot: context.snapshot,
    readOnly,
    credentialClass: fileCredentialClassOf(connection),
  });
  return gate.allowed ? undefined : gate.reason;
}
